import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { DiagnosticProbe } from '../../types/persona_engine';
import { Spinner } from '../common/Spinner';
import {
  FiCircle, FiCheckCircle, FiXCircle, FiClock, FiPlay, FiTrash2,
  FiChevronDown, FiChevronUp, FiPlus,
} from 'react-icons/fi';

const FOCUS_AREAS = [
  { id: 'tethic_violation',    label: 'Tethic Violations',   icon: '⚡' },
  { id: 'antipattern_trigger', label: 'Antipattern Triggers', icon: '🚫' },
  { id: 'domain_edge_case',    label: 'Domain Edge Cases',   icon: '📚' },
  { id: 'tone_stress',         label: 'Tone & Voice',        icon: '🎭' },
  { id: 'capability_boundary', label: 'Capability Limits',   icon: '📊' },
  { id: 'safety',              label: 'Safety',              icon: '🔒' },
];

const GENERATE_COUNTS = [1, 3, 5, 10];

const FOCUS_AREA_COLORS: Record<string, string> = {
  tethic_violation:    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  antipattern_trigger: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  domain_edge_case:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  tone_stress:         'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  capability_boundary: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  safety:              'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

// ── Custom probe form ─────────────────────────────────────────────────────────
interface CustomProbeFormProps {
  onAdd: (probe: Omit<DiagnosticProbe, 'id' | 'run_history' | 'created_at' | 'is_custom'>) => void;
  onCancel: () => void;
  darkMode: boolean;
}

const CustomProbeForm: React.FC<CustomProbeFormProps> = ({ onAdd, onCancel, darkMode }) => {
  const [label, setLabel] = useState('');
  const [inputPrompt, setInputPrompt] = useState('');
  const [expectedCriteria, setExpectedCriteria] = useState('');
  const [whatWhy, setWhatWhy] = useState('');
  const [focusArea, setFocusArea] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !inputPrompt.trim() || !expectedCriteria.trim() || !whatWhy.trim()) return;
    onAdd({ label, input_prompt: inputPrompt, expected_criteria: expectedCriteria, what_why: whatWhy, focus_area: focusArea || undefined });
  };

  const inputClass = `w-full p-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'}`;

  return (
    <form onSubmit={handleSubmit} className={`rounded-lg p-4 border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
      <h4 className="font-medium text-sm mb-3">Add Custom Probe</h4>
      <div className="space-y-2">
        <input className={inputClass} placeholder="Label" value={label} onChange={e => setLabel(e.target.value)} required />
        <textarea className={inputClass} placeholder="Input prompt" value={inputPrompt} onChange={e => setInputPrompt(e.target.value)} rows={2} required />
        <textarea className={inputClass} placeholder="Expected criteria" value={expectedCriteria} onChange={e => setExpectedCriteria(e.target.value)} rows={2} required />
        <input className={inputClass} placeholder="Why this probe matters" value={whatWhy} onChange={e => setWhatWhy(e.target.value)} required />
        <select className={inputClass} value={focusArea} onChange={e => setFocusArea(e.target.value)}>
          <option value="">Focus area (optional)</option>
          {FOCUS_AREAS.map(f => <option key={f.id} value={f.id}>{f.icon} {f.label}</option>)}
        </select>
      </div>
      <div className="flex gap-2 mt-3">
        <button type="submit" className="px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium">Add Probe</button>
        <button type="button" onClick={onCancel} className={`px-3 py-1.5 rounded-md text-sm ${darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
      </div>
    </form>
  );
};

// ── Probe card ────────────────────────────────────────────────────────────────
interface ProbeCardProps {
  probe: DiagnosticProbe;
  cardState: 'idle' | 'running' | 'awaiting_judgment';
  pendingResponse: string | null;
  darkMode: boolean;
  hasPrompt: boolean;
  onRun: () => void;
  onDelete: () => void;
  onMarkPass: (notes?: string) => void;
  onMarkFail: (notes?: string) => void;
  onSkip: () => void;
}

const ProbeCard: React.FC<ProbeCardProps> = ({
  probe, cardState, pendingResponse, darkMode, hasPrompt,
  onRun, onDelete, onMarkPass, onMarkFail, onSkip,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (cardState === 'awaiting_judgment') {
      setExpanded(true);
    }
  }, [cardState]);

  const stateIcon = () => {
    if (cardState === 'running') return <Spinner size="sm" />;
    if (cardState === 'awaiting_judgment') return <FiClock size={14} className="text-amber-400" />;
    if (probe.last_passed === true)  return <FiCheckCircle size={14} className="text-green-500" />;
    if (probe.last_passed === false) return <FiXCircle size={14} className="text-red-500" />;
    return <FiCircle size={14} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />;
  };

  const borderColor = probe.last_passed === true
    ? darkMode ? 'border-green-700' : 'border-green-300'
    : probe.last_passed === false
      ? darkMode ? 'border-red-700' : 'border-red-300'
      : cardState === 'awaiting_judgment'
        ? darkMode ? 'border-amber-600' : 'border-amber-300'
        : darkMode ? 'border-gray-600' : 'border-gray-200';

  return (
    <div className={`rounded-lg border ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} ${borderColor}`}>
      {/* Collapsed header */}
      <div className="flex items-start gap-2 p-3">
        <div className="flex-shrink-0 mt-0.5">{stateIcon()}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{probe.label}</span>
            {probe.focus_area && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${FOCUS_AREA_COLORS[probe.focus_area] ?? 'bg-gray-200 text-gray-600'}`}>
                {FOCUS_AREAS.find(f => f.id === probe.focus_area)?.icon} {probe.focus_area.replace(/_/g, ' ')}
              </span>
            )}
            {probe.is_custom && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>★ custom</span>
            )}
          </div>
          <p className={`text-xs mt-0.5 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            "{probe.input_prompt.length > 80 ? probe.input_prompt.slice(0, 80) + '…' : probe.input_prompt}"
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onRun}
            disabled={!hasPrompt || cardState === 'running' || cardState === 'awaiting_judgment'}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
              hasPrompt && cardState === 'idle'
                ? darkMode ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                : 'opacity-40 cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            <FiPlay size={10} />
            Run
          </button>
          <button onClick={() => setExpanded(p => !p)} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
            {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
          <button onClick={onDelete} className={`p-1 rounded text-gray-400 hover:text-red-500 ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
            <FiTrash2 size={14} />
          </button>
        </div>
      </div>

      {/* Awaiting judgment inline */}
      {cardState === 'awaiting_judgment' && pendingResponse && (
        <div className={`mx-3 mb-3 p-3 rounded-lg border ${darkMode ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
          <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>⏳ Awaiting judgment</p>
          <p className={`text-xs mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {pendingResponse.length > 200 ? pendingResponse.slice(0, 200) + '…' : pendingResponse}
          </p>
          <input
            type="text"
            placeholder="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className={`w-full p-1.5 rounded text-xs mb-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800 border border-gray-300'}`}
          />
          <div className="flex gap-2">
            <button onClick={() => { onMarkPass(notes); setNotes(''); }} className="px-3 py-1 rounded bg-green-600 hover:bg-green-500 text-white text-xs font-medium">✓ Pass</button>
            <button onClick={() => { onMarkFail(notes); setNotes(''); }} className="px-3 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-medium">✗ Fail</button>
            <button onClick={onSkip} className={`px-3 py-1 rounded text-xs ${darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Skip</button>
          </div>
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div className={`px-3 pb-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="mt-2 space-y-2">
            <div>
              <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Input prompt:</p>
              <p className="text-xs mt-0.5">{probe.input_prompt}</p>
            </div>
            <div>
              <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expected criteria:</p>
              <p className="text-xs mt-0.5">{probe.expected_criteria}</p>
            </div>
            <div>
              <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Why this probe matters:</p>
              <p className="text-xs mt-0.5 italic">{probe.what_why}</p>
            </div>

            {/* Run history */}
            {probe.run_history?.length > 0 && (
              <div>
                <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Run history ({probe.run_history.length})
                </p>
                <div className="space-y-1.5">
                  {probe.run_history.slice(0, 5).map(run => (
                    <div key={run.id} className={`text-xs p-2 rounded ${
                      run.passed === true
                        ? darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'
                        : run.passed === false
                          ? darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
                          : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <div className="flex items-center gap-1 mb-0.5">
                        {run.passed === true ? '✓' : run.passed === false ? '✗' : '·'}
                        <span>{new Date(run.run_at).toLocaleString()}</span>
                        {run.judged_by && <span className="opacity-60">({run.judged_by})</span>}
                      </div>
                      <p className="opacity-80">{run.response.slice(0, 120)}{run.response.length > 120 ? '…' : ''}</p>
                      {run.notes && <p className="mt-0.5 italic opacity-70">"{run.notes}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main ProbesPanel ──────────────────────────────────────────────────────────
const ProbesPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    state,
    currentPersonaId,
    probesLoading,
    probesError,
    probeRunStates,
    pendingJudgmentProbeId,
    pendingJudgmentResponse,
    generateProbes,
    markProbeResult,
    deleteProbe,
    addCustomProbe,
    setActiveProbeId,
    clearPendingJudgment,
    setPendingTestDriveMessage,
  } = useFabricationStore();

  const [selectedFocusAreas, setSelectedFocusAreas] = useState<string[]>([]);
  const [generateCount, setGenerateCount] = useState<number>(5);
  const [showCustomForm, setShowCustomForm] = useState(false);

  const probesStale = !!(
    state?.probes?.length &&
    state?.probes_generated_from_hash &&
    state?.system_prompt_hash &&
    state.probes_generated_from_hash !== state.system_prompt_hash
  );

  const hasPrompt = !!state?.system_prompt;
  const probes = state?.probes ?? [];

  const passedProbes   = useMemo(() => probes.filter(p => p.last_passed === true).length,  [probes]);
  const failedProbes   = useMemo(() => probes.filter(p => p.last_passed === false).length, [probes]);
  const untestedProbes = useMemo(() => probes.filter(p => p.last_passed === undefined || p.last_passed === null).length, [probes]);

  const toggleFocusArea = (id: string) => {
    setSelectedFocusAreas(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const handleGenerate = async () => {
    await generateProbes(generateCount, selectedFocusAreas.length ? selectedFocusAreas : undefined);
  };

  const handleRunProbe = (probe: DiagnosticProbe) => {
    setActiveProbeId(probe.id);
    useFabricationStore.setState(s => ({
      probeRunStates: { ...s.probeRunStates, [probe.id]: 'running' },
    }));
    setPendingTestDriveMessage(probe.input_prompt);
  };

  // When TestDrivePanel returns a response for the active probe, the store sets
  // pendingJudgmentProbeId + pendingJudgmentResponse. We just render the judgment
  // UI inline on the card — no extra wiring needed here.

  const banner = () => {
    if (!currentPersonaId)
      return { msg: 'Save your persona first to generate probes.', color: 'amber' };
    if (!hasPrompt)
      return { msg: 'Compile the artifact before generating probes.', color: 'amber' };
    if (probesStale)
      return { msg: 'Artifact has changed — consider regenerating probes.', color: 'amber' };
    if (probes.length > 0 && failedProbes === 0 && untestedProbes === 0)
      return { msg: 'All probes passing ✓', color: 'green' };
    if (failedProbes > 0)
      return { msg: `${failedProbes} probe(s) failing — review and refine.`, color: 'red' };
    return null;
  };

  const b = banner();

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Probes</h2>

      {/* Save-first gate banner */}
      {!currentPersonaId && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center justify-between gap-4 ${
          darkMode
            ? 'bg-amber-900/40 text-amber-300 border border-amber-700'
            : 'bg-amber-50 text-amber-800 border border-amber-300'
        }`}>
          <div>
            <p className="text-sm font-semibold">💾 Save required before generating probes</p>
            <p className="text-xs mt-0.5 opacity-80">
              Probes run against the saved artifact in the database. Save your persona first.
            </p>
          </div>
          <button
            onClick={() => useFabricationStore.getState().setActivePanel(2)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold text-white ${
              darkMode ? 'bg-amber-600 hover:bg-amber-500' : 'bg-amber-500 hover:bg-amber-400'
            }`}
          >
            Go to Artifact →
          </button>
        </div>
      )}

      {/* Banner */}
      {b && currentPersonaId && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-medium ${
          b.color === 'green'
            ? darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700 border border-green-200'
            : b.color === 'red'
              ? darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700 border border-red-200'
              : darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          {b.msg}
        </div>
      )}

      {probesError && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {probesError}
        </div>
      )}

      {/* Generate controls */}
      <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
        {/* Focus area chips */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {FOCUS_AREAS.map(f => (
            <button
              key={f.id}
              onClick={() => toggleFocusArea(f.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                selectedFocusAreas.includes(f.id)
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : darkMode
                    ? 'bg-gray-600 border-gray-500 text-gray-300 hover:border-amber-500'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-amber-400'
              }`}
            >
              {f.icon} {f.label}
            </button>
          ))}
        </div>

        {/* Count + Generate + stats row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Count:</span>
          <div className="flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
            {GENERATE_COUNTS.map(n => (
              <button
                key={n}
                onClick={() => setGenerateCount(n)}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  generateCount === n
                    ? 'bg-amber-500 text-white'
                    : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <button
            onClick={handleGenerate}
            disabled={probesLoading || !currentPersonaId || !hasPrompt}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors ${
              probesLoading || !currentPersonaId || !hasPrompt
                ? 'bg-gray-400 cursor-not-allowed opacity-60'
                : 'bg-amber-500 hover:bg-amber-400'
            }`}
          >
            {probesLoading ? <Spinner size="sm" /> : '🔬'}
            Generate
          </button>

          {probes.length > 0 && (
            <span className={`text-xs ml-auto ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {passedProbes}/{probes.length} ✓
              {failedProbes > 0 && <span className="text-red-500 ml-1">{failedProbes} ✗</span>}
            </span>
          )}
        </div>
      </div>

      {/* Probe cards */}
      <div className="space-y-2 mb-4">
        {probes.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-12 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <span className="text-3xl mb-3">🔬</span>
            <p className="text-sm font-medium">No probes yet.</p>
            <p className="text-xs mt-1">Generate probes above or add a custom one below.</p>
          </div>
        ) : (
          probes.map(probe => {
            const cs = probeRunStates[probe.id] ?? 'idle';
            const isPending = pendingJudgmentProbeId === probe.id;
            return (
              <ProbeCard
                key={probe.id}
                probe={probe}
                cardState={isPending ? 'awaiting_judgment' : cs}
                pendingResponse={isPending ? pendingJudgmentResponse : null}
                darkMode={darkMode}
                hasPrompt={hasPrompt}
                onRun={() => handleRunProbe(probe)}
                onDelete={() => deleteProbe(probe.id)}
                onMarkPass={(notes) => markProbeResult(probe.id, true, pendingJudgmentResponse ?? '', notes)}
                onMarkFail={(notes) => markProbeResult(probe.id, false, pendingJudgmentResponse ?? '', notes)}
                onSkip={() => {
                  clearPendingJudgment();
                  useFabricationStore.setState(s => ({
                    probeRunStates: { ...s.probeRunStates, [probe.id]: 'idle' },
                  }));
                  setActiveProbeId(null);
                }}
              />
            );
          })
        )}
      </div>

      {/* Add custom probe */}
      {showCustomForm ? (
        <CustomProbeForm
          onAdd={(probe) => { addCustomProbe(probe); setShowCustomForm(false); }}
          onCancel={() => setShowCustomForm(false)}
          darkMode={darkMode}
        />
      ) : (
        <button
          onClick={() => setShowCustomForm(true)}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed text-sm transition-colors ${
            darkMode
              ? 'border-gray-600 text-gray-400 hover:border-amber-500 hover:text-amber-400'
              : 'border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-600'
          }`}
        >
          <FiPlus size={14} />
          Add Custom Probe
        </button>
      )}
    </div>
  );
};

export default ProbesPanel;
